<template>
  <div class="money-transfer">
    <el-button v-if="showCreateButton" type="primary" @click="handleCreate">
      生成保证金汇款账号
    </el-button>

    <div v-else class="transfer-content">
      <el-descriptions size="medium" :column="3">
        <el-descriptions-item label="收款户名">
          <span>江西省精彩纵横采购咨询有限公司</span>
        </el-descriptions-item>
        <el-descriptions-item label="汇款账号" :span="2">
          <div v-if="transferInfo.gfAccount">
            <span>{{ transferInfo.gfAccount | bankNoFilter }}</span>
            <span class="text-danger m-l-10">
              该汇款账号具备唯一性，仅适用于当前用户和当前项目，不可用于其他用户和其他项目转账！
            </span>
          </div>
          <span v-else>
            虚拟账号获取失败，
            <el-button style="padding: 0" type="text" @click="getAccount">
              重新获取
            </el-button>
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="开户银行">
          <span>{{ transferInfo.gfAccountBank || '-' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="开户行号">
          <span>{{ transferInfo.gfAccountBankNo | bankNoFilter }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="已缴金额">
          <div>
            <div class="flex col-center">
              <span>{{ transferInfo.accountBalance | moneyFilter }} 元</span>
              <el-button
                v-if="showBalanceNotEnough"
                type="text"
                class="refresh-button m-l-10 m-r-10"
                :disabled="!transferInfo.gfAccount"
                style="padding: 0"
                @click="getAccountBalance"
              >
                刷新到账信息
              </el-button>
              <span v-if="showBalanceTimeout" class="text-danger">
                （未在规定时间内完成缴纳）
              </span>
            </div>
            <money-in-cn :money="transferInfo.accountBalance || '0'" />
          </div>
        </el-descriptions-item>
      </el-descriptions>

      <el-divider />
      <bid-deposit-copywriter
        v-if="guarantee.purchaseAlias === purchaseTypeDict.BIDDING.alias"
      />
      <deposit-copywriter
        v-if="guarantee.purchaseAlias === purchaseTypeDict.INQUIRY.alias"
      />
    </div>
  </div>
</template>

<script>
// 组件
import MoneyInCn from '@/components/common/MoneyInCn.vue'
import BidDepositCopywriter from '@/components/copywriter/BidDepositCopywriter.vue'
import DepositCopywriter from '@/components/copywriter/DepositCopywriter.vue'
// 字典
import { depositBidStatusDict } from '@/dictionary/project/bid'
// 工具
import { bankCardFilter, strToThousands } from '@/utils/utils'
import {
  depositStatusDict,
  purchaseTypeDict
} from '@/dictionary/project/common'

/**
 * @description 现金转账组件
 */
export default {
  name: 'MoneyTransfer',

  components: { MoneyInCn, BidDepositCopywriter, DepositCopywriter },

  filters: {
    bankNoFilter(val) {
      return bankCardFilter(val) || '-'
    },

    moneyFilter(val) {
      return strToThousands(val) || '0'
    }
  },

  props: {
    // 保证金信息
    guarantee: {
      type: Object,
      default: () => {}
    },

    // 转账信息
    info: {
      type: Object,
      required: true
    },

    // 是否使用内部的生成按钮
    showInsideCreateButton: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      purchaseTypeDict,
      showCreateButton: true,

      transferInfo: {
        // 收款户名
        gfAccountName: '江西省精彩纵横采购咨询有限公司',
        // 汇款账号
        gfAccount: '',
        // 开户银行
        gfAccountBank: '',
        // 开户行号
        gfAccountBankNo: '',
        // 已缴纳金额
        accountBalance: ''
      },

      showPopover: true
    }
  },

  computed: {
    // 保证金缴纳不足额
    showBalanceNotEnough() {
      // 如果是招标的保证金，则要根据招标的保证金状态进行判断
      if (this.guarantee.purchaseAlias === purchaseTypeDict.BIDDING.alias) {
        return this.guarantee.gfPayStatus !== depositBidStatusDict.SUCCESS.value
      }
      if (this.guarantee.purchaseAlias === purchaseTypeDict.INQUIRY.alias) {
        return this.guarantee.gfPayStatus !== depositStatusDict.SUCCESS.value
      }
      return true
    },

    // 保证金缴纳超时
    showBalanceTimeout() {
      // 如果是招标的保证金，则要根据招标的保证金状态进行判断
      if (this.guarantee.purchaseAlias === purchaseTypeDict.BIDDING.alias) {
        return (
          this.guarantee.gfPayStatus === depositBidStatusDict.OUT_TIME.value
        )
      }
      return false
    }
  },

  watch: {
    info: {
      handler(val) {
        if (!val) return
        this.transferInfo = Object.assign({}, this.transferInfo, val)
        if (
          val.gfPayStatus === depositBidStatusDict.SUCCESS.value ||
          val.gfPayStatus === depositStatusDict.SUCCESS.value
        ) {
          this.showPopover = false
        }
      },
      deep: true,
      immediate: true
    },

    showInsideCreateButton: {
      handler(val) {
        this.showCreateButton = !!val
      },
      immediate: true
    }
  },

  methods: {
    handleCreate() {
      this.$emit('useMoneyTransfer')
    },

    /**
     * 获取虚拟子账户
     */
    getAccount() {
      this.$emit('getAccount')
    },

    /**
     * 刷新到账信息
     */
    getAccountBalance() {
      this.$emit('getBalance')
    }
  }
}
</script>

<style lang="scss" scoped>
.transfer-content {
  border-radius: 4px;
  border: 1px solid $info-fourth-color;
  background-color: $info-sixth-color;
  padding: 20px;
}

.tip-button--info {
  font-size: 14px;
  color: $info-color;
  cursor: pointer;
  margin-left: 6px;
}

::v-deep .el-descriptions {
  .el-descriptions__body {
    background-color: $info-sixth-color;
  }

  .el-descriptions-item {
    vertical-align: top !important;
    padding-bottom: 16px !important;
  }
}

::v-deep .el-divider--horizontal {
  margin: 0 0 20px;
  background-color: $info-fourth-color;
}
</style>
